/** @jsxImportSource @emotion/react */
import { Box } from '@mui/material'
import { Tab, Tabs } from '@app/components/Molecules/Tabs'
import { DataDrivenBlock, DataDrivenBlocks } from '@app/components/Organisms/DataDrivenBlocks'
import { appLink } from '@app/config/appLink'
import apiClient from '@app/config/axios.config'
import { useFeatureFlags } from '@app/hooks/useFeatureFlags'
import { FEATURE_FLAGS } from '@app/app-constants'
import { lazy, useCallback, useEffect, useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { onboardingPage } from './OnboardingPage.styles'

const UiMarkdown = lazy(() => import('@app/components/Atoms/UiMarkdown/UiMarkdown'))

export const OnboardingPage = () => {
  const clientName = localStorage.getItem('client_name')
  const [pageContent, setPageContent] = useState<
    { section: string; content: string; blocks: DataDrivenBlock[] }[]
  >([])
  const navigate = useNavigate()
  const [tabs, setTabs] = useState<Tab[]>([])
  const { useFeatureFlag } = useFeatureFlags()
  const isOnboardingPageFeatureActive = useFeatureFlag(FEATURE_FLAGS.ONBOARDING_PAGE)
  const isNewLandingPageFeatureActive = useFeatureFlag(FEATURE_FLAGS.NEW_LANDING_PAGE)
  const [selectedTab, setSelectedTab] = useState(0)

  const selectedTabHasBanner = useMemo(() => {
    if (pageContent.length > 0) {
      return pageContent[selectedTab].blocks[0]?.type === 'banner'
    }
    return false
  }, [selectedTab, pageContent])

  useEffect(() => {
    if (!clientName) return
    const abortController = new AbortController()
    const fetchRegions = async () => {
      try {
        const response = await apiClient.get(`/content/onboardingPage`, {
          signal: abortController.signal,
        })

        setPageContent(response.data)
      } catch {
        setPageContent([])
      }
    }
    fetchRegions()
    return () => {
      abortController.abort()
    }
  }, [clientName])

  const renderTabContent = useCallback((content: string) => {
    return <UiMarkdown>{content}</UiMarkdown>
  }, [])

  const renderTabBlocks = (blocks: DataDrivenBlock[]) => {
    return (
      <Box sx={{ height: '100%', overflowY: 'auto' }}>
        <DataDrivenBlocks blocks={blocks} iconFooter={true} />
      </Box>
    )
  }

  useEffect(() => {
    if (!pageContent) return
    const newTabs: Tab[] = pageContent.map((item) => ({
      label: item.section,
      value: item.section,
      content: (
        <Box sx={{ minHeight: '100%' }}>
          {renderTabBlocks(item.blocks)}
          {item.content && renderTabContent(item.content)}
        </Box>
      ),
    }))
    setTabs(newTabs)
  }, [pageContent, renderTabContent])

  useEffect(() => {
    const pageEnabled =
      isOnboardingPageFeatureActive && !!clientName && !isNewLandingPageFeatureActive

    if (!pageEnabled) navigate(appLink.homepage.path)
  }, [navigate, isOnboardingPageFeatureActive, clientName, isNewLandingPageFeatureActive])

  return (
    <>
      <Box css={onboardingPage(selectedTabHasBanner)}>
        <Tabs
          tabs={tabs}
          tabIndex={selectedTab}
          handleChangeTab={(newTabIndex) => {
            setSelectedTab(newTabIndex)
          }}
        />
      </Box>
    </>
  )
}
